import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { apiGetAppointment } from "@/Components/AppointmentModal/apiAppointment";
import { api } from "@/lib/api-client";
import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import { addToast } from "@heroui/react";
import {
  Button,
  Checkbox,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@heroui/react";
import { useForm } from "@mantine/form";
import { useEffect, useState } from "react";
import { MdErrorOutline } from "react-icons/md";
import { RiUserLine } from "react-icons/ri";
import { useCalendarStore } from "../../../store/useCalendarStore";
import { resourceLabel } from "./resourceLabel";
import { NotificationSwitchRow } from "@/Components/AppointmentModal/components/NotificationSettingsSwitchList";
import { ArrowRight, Heart } from "lucide-react";
import dayjs from "dayjs";
import { getStartTimeOfServiceInLinked } from "../../../utils";
import { LimeCollapse } from "@/Components/NextBase/LimeCollapse";
import { useStaffRequestIndicatorVisible } from "@/hooks/useStaffRequestIndicator";
import {
  getDroppedRequestedUserIds,
  getRecordedRequestedUserIds,
  isRecordedRequestOnline,
} from "@/Components/AppointmentModal/components/requestedStaffChange";

type AppointmentDetailsType = {
  appointmentParts: {
    serviceId: number;
    blockBeforeMins: number;
    blockAfterMins: number;
    duration: number;
    resources: number[];
    oldResources: number[];
    users: number[];
    oldUsers: number[];
    appointmentId: number;
    customers?: { requestedUserIds?: number[] }[];
  }[];
  repeatingAppointment?: boolean;
};

export const RescheduleModal = () => {
  const {
    rescheduleData,
    setRescheduleData,
    refreshCalendar,
    hasRescheduleNotification,
    calendarMode,
    users,
    resources,
    services,
  } = useCalendarStore((state) => state);

  const rescheduleForm = useForm({
    initialValues: {
      rescheduleAllLinked: true,
      allLinkedSameUserOrResource: false,
      sendNotifications: true,
      overrideUserService: false,
      overrideResourceService: false,
      updateFutureRepeatingAppointments: false,
    },
  });

  const { mutateAsync: rescheduleAppointment } =
    api.appointments.useRescheduleAppointment();

  const permissions = useUserPermissionsStore((state) => state.permissions);

  const isStaffRequestIndicatorVisible = useStaffRequestIndicatorVisible();

  const [appointment, setAppointment] = useState<AppointmentDetailsType | null>(
    null,
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!rescheduleData?.appointment?.appointmentId) {
      setAppointment(null);
      return;
    }

    checkRescheduleValidity();
  }, [rescheduleData]);

  const checkRescheduleValidity = async () => {
    if (!rescheduleData.appointment) return;

    const { date, startTime, appointmentId, users } =
      rescheduleData.appointment;

    try {
      await rescheduleAppointment({
        appointmentId,
        body: {
          startTime: `${date}T${startTime}`,
          allLinkedSameUserOrResources:
            rescheduleForm.values.allLinkedSameUserOrResource,
          rescheduleAllLinked: rescheduleForm.values.rescheduleAllLinked,
          sendRescheduleNotifications: rescheduleForm.values.sendNotifications,
          updateFutureRepeatingAppointments:
            rescheduleForm.values.updateFutureRepeatingAppointments,
          users,
          resources: rescheduleData.appointment.resources,
          checkOnly: true,
        },
      });

      fetchAppointment();
    } catch (e) {
      addToast({
        title: t`Napaka`,
        color: "danger",
      });

      setRescheduleData(null);
      await refreshCalendar();
    }
  };

  const fetchAppointment = async () => {
    try {
      const response = await apiGetAppointment(
        rescheduleData.appointment.appointmentId,
      );

      const app = response.data.appointment;

      setAppointment(app);
    } catch (e) {
      console.log("error");
    }
  };

  const handleSubmit = async () => {
    const { date, startTime, appointmentId, users } =
      rescheduleData.appointment;

    setIsSubmitting(true);

    try {
      await rescheduleAppointment({
        appointmentId,
        body: {
          startTime: `${date}T${startTime}`,
          allLinkedSameUserOrResources:
            rescheduleForm.values.allLinkedSameUserOrResource,
          rescheduleAllLinked: rescheduleForm.values.rescheduleAllLinked,
          sendRescheduleNotifications: rescheduleForm.values.sendNotifications,
          updateFutureRepeatingAppointments:
            rescheduleForm.values.updateFutureRepeatingAppointments,
          users,
          resources: rescheduleData.appointment.resources,
        },
      });

      addToast({
        title: t`Termin uspešno prestavljen`,
        color: "success",
      });

      setRescheduleData(null);
      await refreshCalendar();
    } catch (e) {
      addToast({
        title: t`Napaka pri prestavljanju termina`,
        color: "danger",
      });
    } finally {
      setIsSubmitting(false);
      setRescheduleData(null);
      await refreshCalendar();
    }
  };

  if (!rescheduleData || !appointment) return null;
  const newAppointmentsWithUsersAndResources =
    appointment.appointmentParts.flatMap((part) => {
      if (
        !rescheduleForm.values.rescheduleAllLinked &&
        part.appointmentId !== rescheduleData.appointment.appointmentId
      )
        return [];

      return [
        {
          ...part,
          oldResources: part.resources,
          oldUsers: part.users,
          users:
            rescheduleForm.values.allLinkedSameUserOrResource ||
            rescheduleData.appointment.appointmentId === part.appointmentId
              ? rescheduleData.appointment.users || part.users
              : part.users,
          resources: rescheduleForm.values.allLinkedSameUserOrResource
            ? rescheduleData.appointment.resources || part.resources
            : part.resources,
          startDuration: part.duration,
          endDuration: null,
          timeOffDuration: null,
          removed: false,
          requestedUserIds: isStaffRequestIndicatorVisible
            ? getRecordedRequestedUserIds(part.customers)
            : [],
          isRequestedOnline:
            isStaffRequestIndicatorVisible &&
            isRecordedRequestOnline(part.customers),
        },
      ];
    });

  const newAppointmentsWithTimes = newAppointmentsWithUsersAndResources.map(
    (part, i) => {
      const service = services.find(
        (s: { serviceId: number }) => s.serviceId === part.serviceId,
      );

      const isUserValidForService =
        calendarMode === "users" && service.doesRequireUser
          ? part.users.every((usr: number) => {
              const userDetails = users.find(
                (u: { userId: number }) => u.userId === usr,
              );

              return userDetails?.userServices.find(
                (us: { serviceId: number }) => us.serviceId === part.serviceId,
              );
            })
          : true;

      const isResourceValidForService =
        calendarMode === "resources"
          ? part.resources.every((res: number) => {
              const resourceDetails = resources.find(
                (r: { resourceId: number }) => r.resourceId === res,
              );

              return resourceDetails?.resourceGroup.resourceGroupServices.find(
                (rgs: { serviceId: number }) =>
                  rgs.serviceId === part.serviceId,
              );
            })
          : true;

      return {
        ...part,
        startTime: getStartTimeOfServiceInLinked({
          selectedServices: newAppointmentsWithUsersAndResources,
          index: i,
          startTime: `${rescheduleData.appointment.date}T${rescheduleData.appointment.startTime}`,
        }),
        isUserValidForService,
        isResourceValidForService,
        serviceName: service.name,
        droppedRequestedUserIds: getDroppedRequestedUserIds({
          requestedUserIds: part.requestedUserIds,
          nextUserIds: part.users,
          isRequestRecorded: part.requestedUserIds.length > 0,
        }),
      };
    },
  );

  const rescheduleUserServiceWarning = newAppointmentsWithTimes.some(
    (app) => !app.isUserValidForService,
  );

  const userFullName = (userId: number) => {
    const user = users.find((u: { userId: number }) => u.userId === userId);
    if (!user) return t`Neznan izvajalec`;
    return `${user.name ?? ""} ${user.lastName ?? ""}`.trim();
  };

  const requestedStaffWarning = newAppointmentsWithTimes.some(
    (app) => app.droppedRequestedUserIds.length > 0,
  );
  const isOnlinePickDropped = newAppointmentsWithTimes.some(
    (app) => app.droppedRequestedUserIds.length > 0 && app.isRequestedOnline,
  );

  const isButtonDisabledRelations = (() => {
    if (permissions.restrict_appointment_services) {
      if (
        rescheduleForm.values.overrideUserService ||
        rescheduleForm.values.overrideResourceService
      ) {
        return true;
      }
      return false;
    }

    if (newAppointmentsWithTimes.some((a) => !a.isUserValidForService)) {
      if (
        rescheduleForm.values.overrideUserService ||
        rescheduleForm.values.overrideResourceService
      ) {
        return false;
      } else {
        return true;
      }
    }
    return false;
  })();

  return (
    <Modal
      isOpen={!!rescheduleData}
      onClose={() => {
        if (rescheduleData) {
          setRescheduleData(null);
          refreshCalendar();
        }
      }}
      title={t`Prestavitev termina`}
    >
      <ModalContent>
        <ModalHeader>{t`Prestavitev termina`}</ModalHeader>
        <ModalBody>
          <form
            className="flex flex-col gap-5"
            onSubmit={rescheduleForm.onSubmit(handleSubmit)}
          >
            <div className="bg-content2 rounded-large flex items-center justify-between gap-3 p-3">
              {rescheduleData?.previousStartTime ? (
                <>
                  <div className="flex flex-col">
                    <span className="text-default-500 text-xs">
                      {dayjs(rescheduleData.previousDate).format(
                        "dd, DD.MM.YYYY",
                      )}
                    </span>
                    <span className="text-default-500 text-sm line-through">
                      {rescheduleData.previousStartTime}
                    </span>
                  </div>

                  <ArrowRight className="text-default-400 size-4 shrink-0" />
                </>
              ) : null}

              <div className="flex flex-col text-right">
                <span className="text-default-500 text-xs">
                  {rescheduleData?.appointment.formattedData.date}
                </span>
                <span className="text-sm leading-5 font-semibold">
                  {rescheduleData?.appointment.formattedData.startTime}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-default-500 text-xs font-medium">
                <Trans>Prestavljate termin za naslednje stranke</Trans>
              </span>

              <div className="flex flex-wrap gap-1.5">
                {rescheduleData?.appointment?.customers.map(
                  (customer: {
                    name: string;
                    lastName: string;
                    id: number;
                  }) => (
                    <span
                      key={customer.id}
                      className="bg-content2 flex items-center gap-1.5 rounded-full py-1 pr-3 pl-2 text-sm"
                    >
                      <RiUserLine
                        className="text-default-500 shrink-0"
                        size={".85rem"}
                      />
                      {customer.name} {customer.lastName}
                    </span>
                  ),
                )}
              </div>
            </div>

            {hasRescheduleNotification && (
              <div className="flex flex-col gap-2">
                <span className="text-default-500 text-xs font-medium">
                  <Trans>Pošlji obvestila?</Trans>
                </span>

                <NotificationSwitchRow
                  label={t`Ob spremembi termina`}
                  isSelected={rescheduleForm.values.sendNotifications}
                  onValueChange={(checked) =>
                    rescheduleForm.setFieldValue("sendNotifications", checked)
                  }
                />
              </div>
            )}

            {rescheduleData?.appointment?.linkedAppointmentId && (
              <div className="flex flex-col gap-2">
                <span className="text-default-500 text-xs font-medium">
                  <Trans>Termin je povezan z ostalimi</Trans>
                </span>

                <div className="flex flex-col gap-1">
                  <NotificationSwitchRow
                    label={t`Prestavi vse povezane termine?`}
                    isSelected={rescheduleForm.values.rescheduleAllLinked}
                    onValueChange={(checked) =>
                      rescheduleForm.setFieldValue(
                        "rescheduleAllLinked",
                        checked,
                      )
                    }
                  />
                  <LimeCollapse
                    isOpen={rescheduleForm.values.rescheduleAllLinked}
                  >
                    <NotificationSwitchRow
                      label={t`Prestavi vse povezane termine k istemu zaposlenemu?`}
                      isSelected={
                        rescheduleForm.values.allLinkedSameUserOrResource
                      }
                      onValueChange={(checked) =>
                        rescheduleForm.setFieldValue(
                          "allLinkedSameUserOrResource",
                          checked,
                        )
                      }
                    />
                  </LimeCollapse>
                </div>
              </div>
            )}

            {appointment?.repeatingAppointment && (
              <div className="flex flex-col gap-2">
                <span className="text-default-500 text-xs font-medium">
                  <Trans>Termin je del ponavljajočih terminov</Trans>
                </span>

                <NotificationSwitchRow
                  label={t`Posodobi vse prihodnje ponavljajoče termine`}
                  description={t`Posodobili se bodo samo začetni časi terminov in zaposleni ter sredstva.`}
                  isSelected={
                    rescheduleForm.values.updateFutureRepeatingAppointments
                  }
                  onValueChange={(checked) =>
                    rescheduleForm.setFieldValue(
                      "updateFutureRepeatingAppointments",
                      checked,
                    )
                  }
                />
              </div>
            )}

            <div className="flex flex-col gap-2">
              <span className="text-default-500 text-xs font-medium">
                <Trans>Storitve v terminu</Trans>
              </span>

              <LimeCollapse isOpen={requestedStaffWarning}>
                <div className="mb-1 flex items-start gap-2 rounded-large border border-warning-200 bg-warning-50 p-3">
                  <Heart className="size-4 shrink-0 translate-y-0.5 text-warning" />
                  <p className="text-sm text-warning-700 leading-5">
                    {isOnlinePickDropped ? (
                      <Trans>
                        Stranka je tega izvajalca izbrala ob spletni
                        rezervaciji. Ste prepričani, da ga želite zamenjati?
                      </Trans>
                    ) : (
                      <Trans>
                        Stranka je želela točno tega izvajalca. Ste prepričani,
                        da ga želite zamenjati?
                      </Trans>
                    )}
                  </p>
                </div>
              </LimeCollapse>

              <LimeCollapse isOpen={rescheduleUserServiceWarning}>
                <div className="border-danger-200 bg-danger-50 rounded-large mb-1 flex flex-wrap items-center justify-between gap-2 border p-3">
                  <p className="text-danger text-sm leading-5 font-medium">
                    <Trans>Zaposleni ne izvajajo storitev</Trans>
                  </p>
                  {!permissions.restrict_appointment_services ? (
                    <Checkbox
                      isSelected={rescheduleForm.values.overrideUserService}
                      onValueChange={(checked) =>
                        rescheduleForm.setFieldValue(
                          "overrideUserService",
                          checked,
                        )
                      }
                    >
                      {t`Prestavi`}
                    </Checkbox>
                  ) : null}
                </div>
              </LimeCollapse>

              {newAppointmentsWithTimes.map((a) =>
                calendarMode === "users" ? (
                  <div
                    key={a.appointmentId}
                    className="bg-content2 rounded-large flex flex-col gap-1 p-3"
                  >
                    <LimeCollapse isOpen={!a.isUserValidForService}>
                      <div className="text-danger mb-2 flex items-center gap-2">
                        <MdErrorOutline />
                        <p className="text-xs leading-4">
                          <Trans>Vsi zaposleni ne izvajajo storitve</Trans>
                        </p>
                      </div>
                    </LimeCollapse>

                    <LimeCollapse isOpen={a.droppedRequestedUserIds.length > 0}>
                      <div className="mb-2 flex items-center gap-2 text-warning-600">
                        <Heart
                          className="size-3.5 shrink-0"
                          fill="currentColor"
                        />
                        <p className="text-xs leading-4">
                          {(() => {
                            const names = a.droppedRequestedUserIds
                              .map(userFullName)
                              .join(", ");
                            if (a.isRequestedOnline) {
                              return t`Stranka je ob spletni rezervaciji izbrala: ${names}`;
                            }

                            return a.droppedRequestedUserIds.length > 1
                              ? t`Stranka je želela točno te izvajalce: ${names}`
                              : t`Stranka je želela točno tega izvajalca: ${names}`;
                          })()}
                        </p>
                      </div>
                    </LimeCollapse>

                    <p className="text-sm leading-5 font-medium">
                      {a.serviceName}
                    </p>

                    <div className="flex items-center gap-2">
                      <div className="flex flex-col">
                        {a.oldUsers.map((user: number) => (
                          <p
                            className="text-default-500 text-sm leading-5 line-through"
                            key={user}
                          >
                            {`${
                              users.find(
                                (u: { userId: number }) => u.userId === user,
                              )?.name || ""
                            } ${
                              users.find(
                                (u: { userId: number }) => u.userId === user,
                              )?.lastName || ""
                            }`}
                          </p>
                        ))}
                      </div>
                      <ArrowRight className="text-default-400 size-4 shrink-0" />
                      <div className="flex flex-col">
                        {a.users.map((user: number) => (
                          <p className="text-sm leading-5" key={user}>
                            {`${
                              users.find(
                                (u: { userId: number }) => u.userId === user,
                              )?.name || ""
                            } ${
                              users.find(
                                (u: { userId: number }) => u.userId === user,
                              )?.lastName || ""
                            }`}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-content2 rounded-large flex flex-col gap-1 p-3">
                    <p className="text-sm leading-5 font-medium">
                      <Trans>Sredstva</Trans>
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col">
                        {rescheduleData?.appointment?.oldResources.map(
                          (resource: number) => (
                            <p
                              className="text-default-500 text-sm leading-5 line-through"
                              key={resource}
                            >
                              {resourceLabel(resources, resource)}
                            </p>
                          ),
                        )}
                      </div>
                      <ArrowRight className="text-default-400 size-4 shrink-0" />
                      <div className="flex flex-col">
                        {rescheduleData?.appointment?.resources.map(
                          (resource: number) => (
                            <p className="text-sm leading-5" key={resource}>
                              {resourceLabel(resources, resource)}
                            </p>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                ),
              )}
            </div>
          </form>
        </ModalBody>
        <ModalFooter>
          <div className="sticky bottom-0 flex justify-end gap-4 border-t border-gray-200 bg-white p-2">
            <Button
              variant="light"
              onPress={() => {
                setRescheduleData(null);
                refreshCalendar();
              }}
              isDisabled={isSubmitting}
            >
              <Trans>Prekliči</Trans>
            </Button>
            <Button
              onPress={handleSubmit}
              isDisabled={isButtonDisabledRelations || isSubmitting}
              isLoading={isSubmitting}
              color="primary"
              data-identifier="confirmRescheduleButton"
            >
              <Trans>Prestavi</Trans>
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
